import { languageNames } from './languageUtils';

// Expanded name database with larger pools for better variety
export const namesByLanguage = {
  'en': {
    male: {
      standard: [
        'James', 'William', 'Oliver', 'Henry', 'Thomas', 'Jack', 'George', 
        'Noah', 'Charlie', 'Oscar', 'Harry', 'Leo', 'Arthur'
      ],
      premium: [
        'Alexander', 'Benjamin', 'Christopher', 'Daniel', 'Edward', 'Frederick',
        'Michael', 'Nicholas', 'Philip', 'Richard', 'Theodore', 'Vincent'
      ],
      hd: [
        'Sebastian', 'Maximilian', 'Harrison', 'Constantine', 'Montgomery', 'Nathaniel',
        'Dominic', 'Gabriel', 'Jonathan', 'Leonardo', 'Raphael', 'Solomon'
      ]
    },
    female: {
      standard: [
        'Emma', 'Charlotte', 'Sophia', 'Olivia', 'Ava', 'Isabella', 'Lucy',
        'Grace', 'Lily', 'Chloe', 'Alice', 'Rose', 'Sarah'
      ],
      premium: [
        'Amelia', 'Elizabeth', 'Victoria', 'Katherine', 'Margaret', 'Caroline',
        'Alexandra', 'Penelope', 'Josephine', 'Beatrice', 'Eleanor', 'Audrey'
      ],
      hd: [
        'Anastasia', 'Cassandra', 'Evangeline', 'Genevieve', 'Isabella', 'Juliette',
        'Madeline', 'Natalia', 'Rosalind', 'Seraphina', 'Valentina', 'Vivienne'
      ]
    }
  },
  'es': {
    male: {
      standard: [
        'Santiago', 'Mateo', 'Lucas', 'Diego', 'Pablo', 'Leonardo',
        'Emilio', 'Adrián', 'Carlos', 'Tomás', 'Hugo', 'Mario'
      ],
      premium: [
        'Alejandro', 'Sebastián', 'Gabriel', 'Nicolás', 'Miguel', 'Fernando',
        'Ricardo', 'Eduardo', 'Antonio', 'Francisco', 'Javier', 'Roberto'
      ],
      hd: [
        'Maximiliano', 'Valentino', 'Benjamín', 'Cristóbal', 'Jerónimo', 'Lorenzo',
        'Mauricio', 'Octavio', 'Rodrigo', 'Salvador', 'Teodoro', 'Vicente'
      ]
    },
    female: {
      standard: [
        'Sofía', 'Valentina', 'Emma', 'Luna', 'Julia', 'Sara',
        'Paula', 'Alba', 'Carmen', 'Ana', 'Elena', 'Clara'
      ],
      premium: [
        'Victoria', 'Camila', 'Lucía', 'Martina', 'Daniela', 'Adriana',
        'Catalina', 'Gabriela', 'Marina', 'Valeria', 'Isabel', 'Regina'
      ],
      hd: [
        'Antonella', 'Celestina', 'Esmeralda', 'Florencia', 'Isabella', 'Marisol',
        'Natalia', 'Paloma', 'Rosario', 'Silvana', 'Teresa', 'Violeta'
      ]
    }
  },
  'fr': {
    male: {
      standard: [
        'Louis', 'Gabriel', 'Jules', 'Hugo', 'Paul', 'Victor',
        'Simon', 'Martin', 'Léon', 'Ethan', 'Oscar', 'Adam'
      ],
      premium: [
        'Arthur', 'Antoine', 'Thomas', 'Léo', 'Raphaël', 'Alexandre',
        'Maxime', 'Charles', 'Vincent', 'Nicolas', 'Laurent', 'Henri'
      ],
      hd: [
        'Augustin', 'Bastien', 'Célestin', 'Dorian', 'Édouard', 'Florian',
        'Gaston', 'Lucien', 'Mathieu', 'Philippe', 'Sébastien', 'Théodore'
      ]
    },
    female: {
      standard: [
        'Emma', 'Louise', 'Alice', 'Chloé', 'Léa', 'Inès',
        'Sarah', 'Anna', 'Clara', 'Eva', 'Nina', 'Rose'
      ],
      premium: [
        'Manon', 'Camille', 'Juliette', 'Charlotte', 'Marie', 'Sophie',
        'Adèle', 'Céline', 'Elodie', 'Isabelle', 'Margot', 'Pauline'
      ],
      hd: [
        'Amélie', 'Béatrice', 'Céleste', 'Delphine', 'Élise', 'Florence',
        'Geneviève', 'Héloïse', 'Joséphine', 'Madeleine', 'Séraphine', 'Violette'
      ]
    }
  }
};

// Map language codes to their base language for name selection
const languageMap: { [key: string]: string } = {
  'en-US': 'en', 'en-GB': 'en', 'en-AU': 'en', 'en-CA': 'en', 'en-IN': 'en',
  'es-ES': 'es', 'es-US': 'es', 'es-MX': 'es',
  'fr-FR': 'fr', 'fr-CA': 'fr',
  'de-DE': 'en', 'de-AT': 'en', 'de-CH': 'en',
  'it-IT': 'en',
  'pt-BR': 'es', 'pt-PT': 'es',
  'nl-NL': 'en',
  'pl-PL': 'en',
  'ru-RU': 'en',
  'ja-JP': 'en',
  'ko-KR': 'en',
  'zh-CN': 'en', 'zh-TW': 'en',
  'ar-XA': 'en',
  'hi-IN': 'en',
  'tr-TR': 'en',
  'id-ID': 'en',
  'vi-VN': 'en',
  'th-TH': 'en',
  'fil-PH': 'en'
};

// Global tracker for all used names to ensure uniqueness across all languages
const globalUsedNames = new Set<string>();

// Get a deterministic but unique name based on voice ID and gender
export const getVoiceName = (voiceId: string, gender: 'male' | 'female'): string => {
  // Get the base language code from the full language code
  const fullLanguage = voiceId.split('-')[0];
  const language = languageMap[`${fullLanguage}-XX`] || languageMap[voiceId.split('-').slice(0, 2).join('-')] || 'en';
  
  const type = voiceId.includes('Neural') ? 'hd' : 
               voiceId.includes('Standard') ? 'standard' : 'premium';
               
  const names = namesByLanguage[language];
  const genderNames = names[gender][type];
  
  // Create a deterministic hash from the voice ID
  const hash = Array.from(voiceId).reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);

  // Try to find an unused name
  const startIndex = Math.abs(hash) % genderNames.length;
  for (let i = 0; i < genderNames.length; i++) {
    const index = (startIndex + i) % genderNames.length;
    const name = genderNames[index];
    
    if (!globalUsedNames.has(name)) {
      globalUsedNames.add(name);
      return name;
    }
  }

  // If all names are used, reset the global tracker and start over
  globalUsedNames.clear();
  const name = genderNames[startIndex];
  globalUsedNames.add(name);
  return name;
};

// Generate a natural preview message
export const generatePreviewMessage = (name: string, languageCode: string): string => {
  const language = languageNames[languageCode]?.split(' (')[0] || 'English';
  
  const messages = [
    `Hi, I'm ${name}. I'm a professional ${language} voice artist ready to bring your content to life.`,
    `Hello! My name is ${name}, and I'm here to help you create engaging ${language} content.`,
    `Greetings! I'm ${name}, a natural-sounding ${language} voice for your projects.`,
    `Hi there! I'm ${name}, and I specialize in creating authentic ${language} voiceovers.`
  ];

  // Use voice ID to consistently select the same message
  const hash = name.split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);

  return messages[Math.abs(hash) % messages.length];
};